import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { OnboardingType } from '@tiendanube/common';
import { useAppDispatch } from 'App/store';
import { useStorage } from 'commons/hooks';
import { useGetStoreInfo, useIsMobileDevice } from 'domains/Auth/hooks';
import {
  getOnboardingAction,
  getOnboardingData,
  getOnboardingStatus,
  updateOnboardingTaskSetupPaymentsAction,
} from 'domains/Dashboard/slices/onBoardingSlice';
import {
  PRE_ONBOARDING_QUESTIONNAIRE_COMPLETED,
  PRE_ONBOARDING_RELEASE_DATE,
} from 'domains/PreOnboarding/hooks';

const ONBOARDING_POS_TYPE_ITEM = 'onboarding-pos-type';

function useOnboardingTasks() {
  const dispatch = useAppDispatch();
  const onboardingTasks = useSelector(getOnboardingData);
  const isMobileMode = useIsMobileDevice();

  const { id, createdAt } = useGetStoreInfo();
  const status = useSelector(getOnboardingStatus);
  const localStorageOnbPosType = `${ONBOARDING_POS_TYPE_ITEM}-${id}`;
  const [onboardingType, setOnboardingType] = useStorage<OnboardingType | ''>(
    localStorageOnbPosType,
    '',
  );

  const localStorageQuestionnaireItem = `${PRE_ONBOARDING_QUESTIONNAIRE_COMPLETED}-${id}`;
  const [questionnaireCompleted, _, loaded] = useStorage(
    localStorageQuestionnaireItem,
    false,
  );

  const getOnboarding = useCallback(() => {
    // Get onboarding only if store has questionnaire completed flag in local storage
    const isStoreCreationBeforePreOnb =
      new Date(createdAt) < new Date(PRE_ONBOARDING_RELEASE_DATE);

    if (isStoreCreationBeforePreOnb || (loaded && questionnaireCompleted)) {
      dispatch(getOnboardingAction({ isMobile: isMobileMode }));
    }
  }, [createdAt, dispatch, isMobileMode, loaded, questionnaireCompleted]);

  const resolvedOnboardingType: OnboardingType = (() => {
    if (onboardingType) return onboardingType;
    if (onboardingTasks?.type) return onboardingTasks.type as OnboardingType;
    return 'online_store';
  })();
  const isPos = onboardingType === 'pos' || onboardingTasks?.type === 'pos';
  const isChat = onboardingType === 'chat' || onboardingTasks?.type === 'chat';
  const isPoschat =
    onboardingType === 'pos_chat' || onboardingTasks?.type === 'pos_chat';
  const isOnlineStore =
    onboardingTasks?.type === 'online_store' ||
    onboardingType === 'online_store' ||
    (!isPos && !isChat && !isPoschat);

  const hasCompleted = onboardingTasks?.status === 'completed';

  const hasCompletedOrSkipped =
    hasCompleted || onboardingTasks?.status === 'skipped';

  const hasCompletedSetupPayments =
    hasCompleted ||
    onboardingTasks?.tasks.find((item) => item.name === 'setup_payments')
      ?.completed;

  const updateOnboardingTaskSetupPayments = () => {
    dispatch(updateOnboardingTaskSetupPaymentsAction());
  };

  useEffect(() => {
    if (onboardingTasks?.type) setOnboardingType(onboardingTasks?.type);
  }, [onboardingTasks?.type, setOnboardingType]);

  const hasStartedOnboarding =
    !hasCompleted && onboardingTasks?.tasks.some((task) => task.completed);

  const onboardingStatus = onboardingTasks?.status || 'pending';

  return {
    ...status,
    onboardingStatus,
    hasCompleted,
    hasCompletedOrSkipped,
    onboardingTasks,
    getOnboarding,
    hasCompletedSetupPayments,
    updateOnboardingTaskSetupPayments,
    hasStartedOnboarding,
    isPos,
    isChat,
    isPoschat,
    isOnlineStore,
    onboardingType: resolvedOnboardingType,
  };
}

export default useOnboardingTasks;
